<script setup lang="ts">
import { computed } from "vue"
import { Paperclip } from "lucide-vue-next"
import VoiceAvatar from "~/components/voice-ui/VoiceAvatar/VoiceAvatar.vue"
import { useGroupsStore } from "~/stores/groups"
import { resolveMediaUrl } from "~/lib/mediaCache"
import { getRoleColor } from "~/lib/chat/roleColor"
import { hitPreviewHtml } from "~/lib/chat/searchHighlight"
import { buildMentionLookups } from "~/lib/chat/mentionTokens"
import type { MessageSearchHit } from "~/stores/messageSearch"

// One search result: author, when, and the matching part of the message with the matches marked.
// Clicking it opens the message in its channel (MessageSearchPanel.vue).

const props = defineProps<{
  hit: MessageSearchHit
  groupId?: string | null
}>()

const emit = defineEmits<{ open: [] }>()

const groupsStore = useGroupsStore()

const author = computed(() => props.hit.message.author)
const member = computed(() =>
  props.groupId ? groupsStore.groupUsers[props.groupId]?.find((u) => u.id === author.value?.id) : undefined)
const name = computed(() => member.value?.display_name || author.value?.display_name || author.value?.username || "Пользователь")
const nameColor = computed(() =>
  props.groupId && member.value ? getRoleColor(groupsStore.groupRoles[props.groupId], member.value.role_ids) : undefined)
const avatarUrl = computed(() => resolveMediaUrl(member.value?.avatar_url ?? author.value?.avatar_url, "avatars"))

const when = computed(() => {
  const raw = props.hit.message.created_at
  if (!raw) return ""
  const date = new Date(raw)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
})

// markdown-free preview: escaped text, only the server's match markers become <mark>.
const previewHtml = computed(() => hitPreviewHtml(
  props.hit.highlights,
  props.hit.message.content ?? "",
  300,
  buildMentionLookups(props.hit.message, props.groupId),
))
const attachmentCount = computed(() => props.hit.message.attachments?.length ?? 0)
</script>

<template>
  <button
      type="button"
      data-testid="message-search-hit"
      :data-message-id="hit.message.id"
      class="group/hit flex w-full gap-3 rounded-xl border border-transparent p-3 text-left transition-colors hover:bg-foreground/5 focus-visible:border-ring focus-visible:outline-none"
      @click="emit('open')"
  >
    <VoiceAvatar class="size-8 shrink-0" :src="avatarUrl" :alt="name" :fallback="name.slice(0, 2).toUpperCase()"/>
    <div class="min-w-0 flex-1">
      <div class="flex items-baseline gap-2">
        <span class="truncate text-sm font-medium" :class="{ 'text-foreground': !nameColor }" :style="{ color: nameColor }">{{ name }}</span>
        <span class="shrink-0 text-[11px] text-muted-foreground">{{ when }}</span>
      </div>
      <!-- eslint-disable-next-line vue/no-v-html -- escaped in hitPreviewHtml, only <mark> added -->
      <p
          v-if="previewHtml"
          class="mt-0.5 line-clamp-4 text-sm leading-relaxed break-words whitespace-pre-wrap text-foreground/90 [&_mark]:rounded-sm [&_mark]:bg-amber-300/40 [&_mark]:px-0.5 [&_mark]:text-foreground dark:[&_mark]:bg-amber-400/30"
          v-html="previewHtml"
      />
      <p v-if="attachmentCount" class="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <Paperclip class="size-3.5"/>
        {{ attachmentCount === 1 ? hit.message.attachments[0]?.file_name : `Вложений: ${attachmentCount}` }}
      </p>
    </div>
  </button>
</template>
